import { useEffect, useState } from "react";

type Action = { label: string; onClick: () => void };
type Msg = { id: number; text: string; action?: Action; duration: number };
const listeners = new Set<(m: Msg) => void>();
let seq = 0;

/** Short status message at the bottom of the screen. With `action` it becomes an undo-style toast:
 * stays for `duration` ms (default 5 s) and shows a button. */
export function toast(text: string, opts?: { action?: Action; duration?: number }): void {
  const m = { id: ++seq, text, action: opts?.action, duration: opts?.duration ?? (opts?.action ? 5000 : 2200) };
  listeners.forEach((fn) => fn(m));
}

/** Run `commit` after a grace period unless the user presses «Отменить» (then `undo` runs instead).
 * Used for destructive actions so a misclick can still be taken back. */
export function withUndo(text: string, commit: () => void, undo: () => void, ms = 5000): void {
  let cancelled = false;
  const timer = window.setTimeout(() => {
    if (!cancelled) commit();
  }, ms);
  toast(text, {
    duration: ms,
    action: {
      label: "Отменить",
      onClick: () => {
        cancelled = true;
        window.clearTimeout(timer);
        undo();
      },
    },
  });
}

export function Toaster() {
  const [items, setItems] = useState<Msg[]>([]);
  useEffect(() => {
    const fn = (m: Msg) => {
      setItems((cur) => [...cur, m]);
      setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== m.id)), m.duration);
    };
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  const close = (id: number) => setItems((cur) => cur.filter((x) => x.id !== id));
  return (
    <div className="pointer-events-none fixed bottom-20 left-1/2 z-[100] flex -translate-x-1/2 flex-col items-center gap-1.5 md:bottom-5 print:hidden">
      {items.map((m) => (
        <div
          key={m.id}
          role="status"
          className="pointer-events-auto flex animate-fade-in items-center gap-3 rounded-lg bg-text px-3.5 py-2 text-sm font-medium text-bg shadow-pop"
        >
          <span>{m.text}</span>
          {m.action && (
            <button
              type="button"
              onClick={() => {
                m.action!.onClick();
                close(m.id);
              }}
              className="rounded-md px-2 py-0.5 font-semibold text-[rgb(147_197_253)] hover:bg-white/10"
            >
              {m.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
